import { Link } from 'react-router-dom';
import BlueDotsBackground from '../BlueDotsBackground';

export default function Trial() {
  const trialSteps = [
    {
      title: 'Kickoff & Connectivity',
      items: [
        'Define participating states (or all states)',
        'Configure access requirements',
        'Review trial objectives',
        'Review current process',
      ],
      icon: 'fas fa-plug',
    },
    {
      title: 'Invoice & CLD Retrieval',
      items: [
        'Automated collection of available files (tested with 2026Q2)',
        'Tracking of receipt status',
        'Centralized repository',
      ],
      icon: 'fas fa-file-import',
    },
    {
      title: 'Validation & Review',
      items: [
        'Invoice and CLD validation',
        'Identification of anomalies',
        'Exception reporting',
      ],
      icon: 'fas fa-check-circle',
    },
    {
      title: 'Dispute Support',
      items: [
        'Supporting documentation generation',
        'Audit-ready evidence collection',
        'Dispute tracking and visibility',
      ],
      icon: 'fas fa-scale-balanced',
    },
    {
      title: 'Results Assessment',
      items: [
        'Compare manual vs. automated effort',
        'Measure discrepancies identified',
        'Quantify cycle-time improvements',
        'Review financial opportunities uncovered',
      ],
      icon: 'fas fa-chart-line',
    },
  ];

  const faqs = [
    {
      question: 'Is there a cost for the trial?',
      answer: 'Kernel charges a nominal fee to cover hosting and setup costs.',
    },
    {
      question: 'Do we need to replace our current Medicaid rebate system?',
      answer:
        'No. The trial runs in parallel with your existing Medicaid rebate platform. You keep using your current system throughout the trial, and there are no changes to your rebate processes.',
    },
    {
      question: 'How much work is required from our team?',
      answer:
        'Very little. Your team provides the required system access, identifies key stakeholders, and participates in a kickoff and periodic review meetings. Kernel handles configuration, retrieval, validation, and reporting.',
    },
    {
      question: 'Will the trial impact invoice payments?',
      answer:
        'No. You retain full responsibility for submissions and payments. The trial only retrieves, validates, and documents invoice and CLD data in parallel, so it never interferes with your payment workflow.',
    },
    {
      question: 'How is data secured?',
      answer:
        'Data is accessed through secure connections and stored in a centralized, controlled repository. Access is limited to the agreed states and Q3 2026 project scope, with credentials delivered securely before onboarding. Kernel maintains SOC 2 Type II and SOC 1 Type II attestation reports, demonstrating the highest standard of independent security and controls assurance. These attestations are supported by an ongoing audit program and continuous compliance processes designed to uphold rigorous security, confidentiality, and operational control requirements.',
    },
    {
      question: 'How are results measured?',
      answer:
        'Trial outcomes are measured against your current-state performance, including the percentage of manual effort automated, the number and value of discrepancies identified, cycle-time improvements, and financial recovery opportunities uncovered.',
    },
    {
      question: 'What happens after the trial ends?',
      answer:
        'You receive a results review and business case assessment. From there you can decide whether to expand to additional states, quarters, or a broader deployment. There is no obligation to continue.',
    },
  ];

  const testimonials = [
    {
      quote:
        "OpenPharma has provided a level of visibility into our operations that we simply didn't have before. The platform helps us monitor performance, identify trends, and address challenges proactively, allowing our team to make faster, more informed decisions with greater confidence.",
      author: 'Reimbursement Director, Top 20 Pharmaceutical Company',
    },
    {
      quote:
        "OpenPharma's dispute reporting capabilities have been instrumental in improving our rebate dispute resolution process. The detailed insights and supporting data have enabled us to build stronger working relationships with state agencies, resulting in more productive discussions and a higher rate of disputes being resolved in our favor.",
      author: 'Medicaid Analyst',
    },
    {
      quote:
        'OpenPharma has enhanced our ability to monitor and manage rebate disputes while providing greater confidence in the accuracy of our reserves. The increased transparency and insights have been invaluable to our organization.',
      author: 'VP of Market Access',
    },
  ];

  return (
    <>
      <BlueDotsBackground />

      <div className="min-h-screen text-white px-6 py-12 space-y-24">
        {/* Hero */}
        <section className="text-center max-w-5xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Trial the Kernel Medicaid Automation Process During the 2026Q3
            Invoice Cycle
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-10">
            Experience a touchless Medicaid workflow that retrieves, validates,
            documents, and tracks invoices and CLD data alongside your existing
            rebate platform with minimal operational disruption.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                text: 'No replacement of existing Medicaid systems',
                icon: 'fas fa-database',
              },
              {
                text: 'No changes to current rebate processes',
                icon: 'fas fa-arrows-rotate',
              },
              {
                text: 'Automated invoice and CLD retrieval',
                icon: 'fas fa-file-import',
              },
              {
                text: 'Validation and discrepancy identification',
                icon: 'fas fa-magnifying-glass-chart',
              },
              {
                text: 'Operational and financial visibility',
                icon: 'fas fa-chart-line',
              },
              {
                text: 'Trial results measured against current-state performance',
                icon: 'fas fa-scale-balanced',
              },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-5"
              >
                <i className={`${item.icon} text-blue-400 mb-3`}></i>
                <p>{item.text}</p>
              </div>
            ))}
          </div>

          <Link
            to="/contact"
            className="inline-block mt-10 bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition"
          >
            Schedule a Trial Planning Session
          </Link>
        </section>

        {/* Purpose */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Why We're Offering This Trial
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <h3 className="text-xl font-semibold mb-4 text-blue-400">
              Purpose
            </h3>

            <p className="mb-4">
              The Medicaid invoice cycle continues to place significant
              operational demands on manufacturers. Teams often spend valuable
              time collecting files, validating data, documenting disputes, and
              preparing for deadlines while simultaneously closing the prior
              quarter.
            </p>

            <p>
              The 2026Q3 Trial Program allows organizations to evaluate how an
              automated, agentic Medicaid process can reduce manual effort,
              improve controls, and provide greater visibility without
              disrupting existing operations.
            </p>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            How the Trial Works
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-6">
              During the 2026Q3 invoice cycle, Kernel's OpenPharma platform
              will:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Retrieve state Medicaid invoices',
                'Retrieve CLD files',
                'Validate invoice and utilization data',
                'Identify exceptions and discrepancies',
                'Generate supporting documentation',
                'Track disputes and resolutions',
                'Provide operational and financial reporting',
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-black/30 rounded-lg p-4 border border-gray-800"
                >
                  {index + 1}. {item}
                </div>
              ))}
            </div>

            <p className="mt-6 text-gray-300">
              The trial operates in parallel with your existing Medicaid rebate
              process, allowing your team to compare outcomes without
              introducing risk to current operations.
            </p>
          </div>
        </section>

        {/* Trial Process */}
        <section className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            Trial Process
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {trialSteps.map((step, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6"
              >
                <i className={`${step.icon} text-blue-400 text-3xl mb-4`}></i>

                <h3 className="text-xl font-semibold mb-4">
                  Step {index + 1}: {step.title}
                </h3>

                <ul className="space-y-2 text-gray-300">
                  {step.items.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Proposed Timeline
          </h2>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              ['August - September 2026', 'Planning and onboarding'],
              ['October 2026', 'System configuration and readiness review'],
              ['November 2026', '2026Q3 invoice cycle processing'],
              ['February 2027', 'Results review and business case assessment'],
            ].map(([date, text], index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6 text-center"
              >
                <h3 className="text-blue-400 font-semibold mb-3">{date}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Roles */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Roles & Responsibilities
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Customer Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Provide required system access</li>
                <li>• Identify key stakeholders</li>
                <li>• Participate in kickoff and review meetings</li>
                <li>• Validate trial results</li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Kernel / OpenPharma Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Configure OpenPharma</li>
                <li>• Manage invoice and CLD retrieval</li>
                <li>• Execute validations</li>
                <li>• Deliver operational reporting</li>
                <li>• Present trial results</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Assumptions */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Assumptions & Requirements
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Assumptions
              </h3>

              <ul className="space-y-2">
                <li>• Existing Medicaid rebate process remains unchanged</li>
                <li>
                  • Customer retains responsibility for submissions and payments
                </li>
                <li>
                  • Required access credentials provided before onboarding
                </li>
                <li>• Limited to agreed states and 2026Q3 scope</li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Technical Requirements
              </h3>

              <ul className="space-y-2">
                <li>• Secure access to invoice retrieval sources</li>
                <li>• Periodic review meetings with business contact</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Success Criteria */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Success Criteria
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-6">
              By the end of the trial, participants will understand:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {[
                'Percentage of manual effort that can be automated',
                'Number and value of discrepancies identified',
                'Improvements in process visibility',
                'Potential reduction in invoice processing cycle time',
                'Financial recovery opportunities discovered',
                'Readiness for broader deployment',
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-black/30 border border-gray-800 rounded-lg p-4"
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-900 border border-gray-700 rounded-xl p-5"
              >
                <summary className="flex items-center justify-between font-semibold cursor-pointer list-none">
                  <span>{faq.question}</span>
                  <i className="fas fa-chevron-down text-blue-400 ml-4 transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-300">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            What Our Customers Say
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8 flex flex-col"
              >
                <i className="fas fa-quote-left text-blue-400 text-2xl mb-4"></i>

                <p className="text-gray-300 italic flex-grow">
                  {testimonial.quote}
                </p>

                <p className="mt-6 font-semibold text-blue-400">
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="text-center max-w-4xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-10">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Experience a More Efficient Medicaid Process?
            </h2>

            <p className="text-gray-300 mb-8">
              Experience the 2026Q3 Medicaid invoice cycle with automated
              retrieval, validation, dispute support, and visibility while
              maintaining your existing workflow.
            </p>

            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition inline-block"
            >
              Schedule a Trial Planning Session
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
